import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import {
    VStack, HStack, Box, Card, CardBody, Text, IconButton,
    Spinner, Icon, Flex, Textarea, Button, useDisclosure
} from '@chakra-ui/react';
import {
    TbSend, TbSparkles, TbRefresh, TbTrash, TbChevronDown, TbChevronUp,
    TbBuilding, TbTruck, TbShieldCheck, TbCar
} from 'react-icons/tb';
import { useAnalyticsChat } from '../../../hooks/reporting/useAnalyticsChat';
import ChatVisualization from '../components/ChatVisualizations';
import { ChatMessage } from '../../../types/analyticsChat';

// Suggested query type
interface SuggestedQuery {
    text: string;
    category: 'location' | 'vendor' | 'insurance' | 'oem';
}

// Suggested queries organized by category
const SUGGESTED_QUERIES: SuggestedQuery[] = [
    // Location queries
    { text: "Which location made the most profit last month?", category: 'location' },
    { text: "Compare my top 5 locations by order volume", category: 'location' },
    { text: "Show gross margin trend by location over the past 3 months", category: 'location' },
    
    // Vendor queries
    { text: "Who are my top 5 vendors by gross profit?", category: 'vendor' },
    { text: "Which vendor has the best gross margin?", category: 'vendor' },
    { text: "Show me vendor order volume breakdown", category: 'vendor' },
    
    // Insurance queries
    { text: "Which insurance carrier brings the most volume?", category: 'insurance' },
    { text: "Compare gross margins across insurance carriers", category: 'insurance' },
    { text: "Show missed opportunity by insurance carrier", category: 'insurance' },
    
    // OEM queries
    { text: "What's my order volume by vehicle manufacturer?", category: 'oem' },
    { text: "Which OEM has the highest average order size?", category: 'oem' },
    { text: "Show gross profit trend by OEM", category: 'oem' },
];

// Category icons
const categoryIcons: Record<string, any> = {
    location: TbBuilding,
    vendor: TbTruck,
    insurance: TbShieldCheck,
    oem: TbCar,
};

// Category colors
const categoryColors: Record<string, string> = {
    location: 'blue',
    vendor: 'green',
    insurance: 'purple',
    oem: 'orange',
};

interface AskPartBayTabProps {
    startDate: string;
    endDate: string;
    locationId?: string;
}

const AskPartBayTab = ({ startDate, endDate, locationId }: AskPartBayTabProps) => {
    const [input, setInput] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const { isOpen: showAllSuggestions, onToggle: toggleSuggestions } = useDisclosure();

    const {
        messages,
        isLoading,
        sendMessage,
        clearMessages,
        retryLastMessage
    } = useAnalyticsChat();

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input on mount
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleSubmit = async (query?: string) => {
        const queryToSend = query || input.trim();
        if (!queryToSend || isLoading) return;
        
        setInput('');
        await sendMessage(queryToSend);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const handleSuggestionClick = (query: string) => {
        handleSubmit(query);
    };

    // Filter suggestions by category
    const filteredSuggestions = selectedCategory
        ? SUGGESTED_QUERIES.filter(q => q.category === selectedCategory)
        : SUGGESTED_QUERIES;

    const displayedSuggestions = showAllSuggestions
        ? filteredSuggestions
        : filteredSuggestions.slice(0, 6);

    // Render a single chat message
    const renderMessage = (message: ChatMessage) => {
        const isUser = message.role === 'user';
        
        return (
            <Box
                key={message.id}
                alignSelf={isUser ? 'flex-end' : 'flex-start'}
                maxW={isUser ? '70%' : '95%'}
                w={isUser ? 'auto' : '100%'}
            >
                <Card
                    bg={isUser ? '#005BC4' : 'white'}
                    color={isUser ? 'white' : 'gray.800'}
                    borderRadius="xl"
                    boxShadow="none"
                    border={isUser ? 'none' : '1px solid'}
                    borderColor="gray.200"
                >
                    <CardBody py={3} px={4}>
                        <Text whiteSpace="pre-wrap">{message.content}</Text>
                        
                        {/* Visualization for assistant messages with data */}
                        {!isUser && message.response && !message.error && (
                            <Box mt={4}>
                                <ChatVisualization response={message.response} />
                            </Box>
                        )}

                        {/* Error state with retry button */}
                        {!isUser && message.error && (
                            <HStack mt={3}>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    colorScheme="red"
                                    leftIcon={<Icon as={TbRefresh as React.ElementType} />}
                                    onClick={retryLastMessage}
                                >
                                    Retry
                                </Button>
                            </HStack>
                        )}
                    </CardBody>
                </Card>
                
                {/* Timestamp */}
                <Text fontSize="xs" color="gray.400" mt={1} px={2}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Text>
            </Box>
        );
    };

    // Empty state with suggestions
    const renderEmptyState = () => (
        <VStack spacing={6} py={8} px={4}>
            {/* Logo/Icon */}
            <Box
                p={4}
                borderRadius="full"
                bg="linear-gradient(135deg, #0071E3 0%, #005BC4 100%)"
                boxShadow="none"
            >
                <Icon as={TbSparkles as React.ElementType} boxSize={10} color="white" />
            </Box>

            {/* Title */}
            <VStack spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                    Ask Yaxle
                </Text>
                <Text color="gray.500" textAlign="center" maxW="400px">
                    Ask questions about your business data in plain English.
                    I can analyze locations, vendors, insurance carriers, and OEMs.
                </Text>
            </VStack>

            {/* Category filters */}
            <HStack spacing={2} flexWrap="wrap" justify="center">
                <Button
                    size="sm"
                    variant={selectedCategory === null ? 'solid' : 'outline'}
                    colorScheme="gray"
                    onClick={() => setSelectedCategory(null)}
                    borderRadius="full"
                >
                    All
                </Button>
                {Object.entries(categoryIcons).map(([category, IconComponent]) => (
                    <Button
                        key={category}
                        size="sm"
                        variant={selectedCategory === category ? 'solid' : 'outline'}
                        colorScheme={categoryColors[category]}
                        leftIcon={<Icon as={IconComponent as React.ElementType} />}
                        onClick={() => setSelectedCategory(category)}
                        borderRadius="full"
                        textTransform="capitalize"
                    >
                        {category}
                    </Button>
                ))}
            </HStack>

            {/* Suggested queries */}
            <VStack spacing={3} w="100%" maxW="600px">
                <Flex flexWrap="wrap" gap={2} justify="center">
                    {displayedSuggestions.map((query, idx) => (
                        <Box
                            key={idx}
                            as="button"
                            px={4}
                            py={2}
                            bg="gray.50"
                            borderRadius="full"
                            fontSize="sm"
                            color="gray.700"
                            border="1px solid"
                            borderColor="gray.200"
                            _hover={{
                                bg: `${categoryColors[query.category]}.50`,
                                borderColor: `${categoryColors[query.category]}.200`,
                                color: `${categoryColors[query.category]}.700`
                            }}
                            transition="all 0.2s"
                            onClick={() => handleSuggestionClick(query.text)}
                        >
                            <HStack spacing={2}>
                                <Icon
                                    as={categoryIcons[query.category] as React.ElementType}
                                    boxSize={4}
                                    color={`${categoryColors[query.category]}.500`}
                                />
                                <Text>{query.text}</Text>
                            </HStack>
                        </Box>
                    ))}
                </Flex>

                {/* Show more/less toggle */}
                {filteredSuggestions.length > 6 && (
                    <Button
                        size="sm"
                        variant="ghost"
                        color="gray.500"
                        rightIcon={<Icon as={(showAllSuggestions ? TbChevronUp : TbChevronDown) as React.ElementType} />}
                        onClick={toggleSuggestions}
                    >
                        {showAllSuggestions ? 'Show less' : `Show ${filteredSuggestions.length - 6} more`}
                    </Button>
                )}
            </VStack>
        </VStack>
    );

    return (
        <Flex
            direction="column"
            h="100%"
            minH="500px"
            overflow="hidden"
        >
            {/* Messages area */}
            <Box
                flex={1}
                overflowY="auto"
                px={4}
                py={4}
            >
                {messages.length === 0 ? (
                    renderEmptyState()
                ) : (
                    <VStack spacing={4} align="stretch">
                        {messages.map(renderMessage)}
                        
                        {/* Loading indicator */}
                        {isLoading && (
                            <HStack
                                alignSelf="flex-start"
                                bg="white"
                                px={4}
                                py={3}
                                borderRadius="xl"
                                boxShadow="none"
                                border="1px solid"
                                borderColor="gray.200"
                            >
                                <Spinner size="sm" color="#005BC4" />
                                <Text fontSize="sm" color="gray.500">
                                    Analyzing your data...
                                </Text>
                            </HStack>
                        )}
                        
                        <div ref={messagesEndRef} />
                    </VStack>
                )}
            </Box>

            {/* Input area */}
            <Box
                px={4}
                py={4}
                borderTop="1px solid"
                borderColor="gray.200"
                bg="white"
                position="sticky"
                bottom={0}
                flexShrink={0}
                zIndex={10}
            >
                {/* Clear button when there are messages */}
                {messages.length > 0 && (
                    <Flex justify="center" mb={3}>
                        <Button
                            size="xs"
                            variant="ghost"
                            color="gray.400"
                            leftIcon={<Icon as={TbTrash as React.ElementType} />}
                            onClick={clearMessages}
                            _hover={{ color: 'gray.600' }}
                        >
                            Clear conversation
                        </Button>
                    </Flex>
                )}

                <HStack spacing={3}>
                    <Textarea
                        ref={inputRef}
                        placeholder="Ask about your locations, vendors, OEMs, or insurance carriers..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        minH="44px"
                        maxH="120px"
                        resize="none"
                        borderRadius="xl"
                        borderColor="gray.300"
                        _hover={{ borderColor: 'gray.400' }}
                        _focus={{
                            borderColor: '#005BC4',
                            boxShadow: '0 0 0 1px #005BC4'
                        }}
                        disabled={isLoading}
                    />
                    <IconButton
                        aria-label="Send message"
                        icon={<Icon as={TbSend as React.ElementType} boxSize={5} />}
                        onClick={() => handleSubmit()}
                        isLoading={isLoading}
                        isDisabled={!input.trim()}
                        colorScheme="blue"
                        bg="#005BC4"
                        _hover={{ bg: '#0071E3' }}
                        borderRadius="xl"
                        size="lg"
                        h="44px"
                        w="44px"
                    />
                </HStack>

                {/* Keyboard hint */}
                <Text fontSize="xs" color="gray.400" mt={2} textAlign="center">
                    Press Enter to send, Shift+Enter for new line
                </Text>
            </Box>
        </Flex>
    );
};

export default AskPartBayTab;

